// Section · TransactionDocuments
//
// Entity-view "Dokumenty" section. Fetches the transaction's files and lays
// them out as a stack of bucket rows — each row is a label + the SHARED
// `<TransactionDocsBucket>` (same component the pipeline cells use). The
// pills, hover preview, regenerate-button, and photo status grouping all
// come from that single component so the view matches the queue.

import React from "react";
import { useTranslation } from "react-i18next";
import type { Transaction, TransactionFile, Transport } from "@shared/types/transaction";
import type { DocumentType } from "@shared/types";

import { Stack } from "@ds/layout/Stack";
import { Box } from "@ds/layout/Box";
import { Grid } from "@ds/layout/Grid";
import { Eyebrow } from "@ds/display/Eyebrow";
import { Alert } from "@ds/display/Alert";
import { useRunListQuery } from "@ds/config/runQuery";
import { useSubQueryOptional } from "@ds/data/subQueriesContext";

import { TransactionDocsBucket, type GeneratedDocDef } from "../cells/TransactionDocsBucket";
import { transactionFileDataSchema } from "../transactionFileSchema";

// Expected generated-doc types. Mirrors GEN_DOC_DEFS in
// transactionPipelineFields to avoid an import cycle (sections → pipeline
// → sections). Keep in sync; both lists are intentionally short.
const GENERATED_DEFS: ReadonlyArray<{ type: DocumentType; label: string; checkKey: string }> = [
  { type: "transport_order" as DocumentType, label: "Zlecenie",  checkKey: "transport_order_generated_check" },
  { type: "cmr"             as DocumentType, label: "CMR",       checkKey: "cmr_generated_check" },
  { type: "annex7"          as DocumentType, label: "Aneks VII", checkKey: "annex7_generated_check" },
];

function checkOf(r: Transaction, key: string): string | null {
  return (r as unknown as Record<string, string | null>)[key] ?? null;
}

export interface TransactionDocumentsProps {
  record: Transaction;
}

export function TransactionDocuments({ record }: TransactionDocumentsProps): React.ReactElement {
  const { t } = useTranslation();

  // Page-batched sub-query (matches the FILES_SUB_QUERY pipeline cells use)
  // first — if mounted, reuse those rows so we don't double-fetch. Otherwise
  // fall back to a section-local `useRunListQuery`.
  const subFiles = useSubQueryOptional<TransactionFile>("files", record.id);
  const local = useRunListQuery<TransactionFile>({
    schema: transactionFileDataSchema,
    filter: [
      { field: "transaction_id", operator: "eq", value: record.id },
      { field: "is_latest",      operator: "eq", value: true },
    ],
    page: { size: 200 },
    enabled: record.id > 0 && subFiles.rows.length === 0 && !subFiles.isLoading,
  });

  // ── Transports — from the page-batched sub-query (same source the Transport
  //    section uses). The sub-query is registered via `subQueries` in the
  //    section config so EntityViewPage fetches it once for the whole page.
  //    Called before the error early-return so hook order stays stable.
  const subTransports = useSubQueryOptional<Transport>("transports", record.id);

  const isError = local.query.isError;
  const files: TransactionFile[] = subFiles.rows.length > 0 ? subFiles.rows : local.result.data;

  if (isError) {
    return (
      <Box paddingX="lg" paddingY="md" fullWidth>
        <Alert tone="red" title="Nie udało się załadować dokumentów" />
      </Box>
    );
  }

  const transports: Transport[] = subTransports.rows;
  const transportDone = checkOf(record, "transport_complete_check") === "completed";
  const generatedDefs: GeneratedDocDef[] = GENERATED_DEFS.map((d) => ({
    type: d.type,
    label: d.label,
    checkStatus: checkOf(record, d.checkKey),
  }));

  const generatedFiles = files.filter((f) => f.document_status === "generated");
  const copies = files.filter(
    (f) => f.document_status === "copy" ||
      (f.document_type === "other" && f.document_status !== "generated"),
  );
  const originals = files.filter(
    (f) => f.document_status === "original" && f.document_type !== "waste_image",
  );
  const customs = files.filter(
    (f) => f.document_type === "ie529" || f.document_type === "ie599",
  );
  const photos = files.filter((f) => f.document_type === "waste_image");

  const rows: Array<{ id: string; label: string; node: React.ReactNode }> = [
    {
      id:    "generated",
      label: t("transactions.documents.generated", "Wygenerowane"),
      node:  <TransactionDocsBucket
               mode="generated"
               files={generatedFiles}
               transactionId={record.id}
               defs={generatedDefs}
               transports={transports}
               transportDone={transportDone}
             />,
    },
    {
      id:    "copies",
      label: t("transactions.documents.copies", "Kopie"),
      node:  <TransactionDocsBucket
               mode="uploaded"
               files={copies}
               transactionId={record.id}
               referenceNumber={record.reference_number}
               checkPending={checkOf(record, "annex_copy_approved_check") === "pending"}
               uploadStatus="copy"
             />,
    },
    {
      id:    "originals",
      label: t("transactions.documents.originals", "Oryginały"),
      node:  <TransactionDocsBucket
               mode="uploaded"
               files={originals}
               transactionId={record.id}
               referenceNumber={record.reference_number}
               checkPending={
                 checkOf(record, "annex_original_approved_check") === "pending" ||
                 checkOf(record, "cmr_original_approved_check") === "pending"
               }
               uploadStatus="original"
             />,
    },
  ];
  if (record.border_type === "non_eu") {
    rows.push({
      id:    "customs",
      label: t("transactions.documents.customs", "Celne"),
      node:  <TransactionDocsBucket
               mode="uploaded"
               files={customs}
               transactionId={record.id}
               checkPending={checkOf(record, "customs_docs_approved_check") === "pending"}
               uploadStatus="original"
               useCustomsTypePicker
             />,
    });
  }
  rows.push({
    id:    "photos",
    label: t("transactions.documents.photos", "Zdjęcia"),
    node:  <TransactionDocsBucket
             mode="photos"
             files={photos}
             transactionId={record.id}
             checkPending={checkOf(record, "loading_photo_approved_check") === "pending"}
           />,
  });

  return (
    <Stack gap="none" divider="soft" testId="transaction-documents-section">
      {rows.map((row) => (
        <Box key={row.id} size="md">
          <Grid columns="label-value" gap="md" align="center">
            <Eyebrow>{row.label}</Eyebrow>
            {row.node}
          </Grid>
        </Box>
      ))}
    </Stack>
  );
}
